import { useCallback, useEffect, useState } from 'react';
import { Quote, Save, Loader2, EyeOff, Eye, Search, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const QueueSettings = () => {
  const [s, setS] = useState({ quota: '', window_hours: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/admin/summaries/settings').then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/admin/summaries/settings', { quota: Number(s.quota), window_hours: Number(s.window_hours) }); toast.success(r.data.message); }
    catch (e) { toast.error(e.response?.data?.detail || 'فشل الحفظ'); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 space-y-3" data-testid="summary-queue-settings">
      <h3 className="font-bold text-accent flex items-center gap-2"><Send className="w-4 h-4 text-primary" /> الإرسال إلى جوجل شيت</h3>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label className="space-y-1"><span className="text-xs text-muted-foreground">عدد الملخصات</span>
          <Input type="number" min="1" max="100" value={s.quota} onChange={(e) => setS({ ...s, quota: e.target.value })} className="w-28" data-testid="queue-quota-input" /></label>
        <span className="pb-2 text-muted-foreground">كل</span>
        <label className="space-y-1"><span className="text-xs text-muted-foreground">ساعة</span>
          <Input type="number" min="1" max="168" value={s.window_hours} onChange={(e) => setS({ ...s, window_hours: e.target.value })} className="w-28" data-testid="queue-hours-input" /></label>
        <Button onClick={save} disabled={saving} className="gap-1" data-testid="queue-settings-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} حفظ</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">فترة متحركة على مستوى المنصة كلها، والباقي ينتظر دوره بالأقدم أولاً.</p>
    </Card>
  );
};

const Row = ({ item, onToggle, busy }) => (
  <div className={`p-4 border-b last:border-b-0 flex flex-col md:flex-row gap-3 ${item.hidden ? 'bg-red-50/40' : ''}`} data-testid={`admin-summary-row-${item.id}`}>
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-accent">{item.title || 'بلا عنوان'}</span>
        {item.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted">{item.category}</span>}
        {item.hidden && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">محجوب</span>}
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${item.synced_to_sheets ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
          {item.synced_to_sheets ? 'أُرسل للشيت' : 'في طابور الشيت'}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{item.reader_name} · #{item.reader_number} {item.name_hidden && (item.reader_gender === 'female' ? '· اختارت إخفاء اسمها' : '· اختار إخفاء اسمه')} · {item.likes_count} إعجاب · {item.published_at ? new Date(item.published_at).toLocaleDateString('ar-OM') : ''}</p>
      <p className="text-sm leading-relaxed line-clamp-3 whitespace-pre-line">{item.text}</p>
    </div>
    <Button variant="outline" disabled={busy} onClick={() => onToggle(item)}
      className={`shrink-0 gap-1 ${item.hidden ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}`} data-testid={`admin-summary-toggle-${item.id}`}>
      {item.hidden ? <><Eye className="w-4 h-4" /> إظهار</> : <><EyeOff className="w-4 h-4" /> حجب</>}
    </Button>
  </div>
);

export default function AdminSummariesPage() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [data, setData] = useState({ items: [], total: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  const load = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/summaries', { params: { q: debounced, visibility, skip, limit: 30 } });
      setData((prev) => (skip ? { ...r.data, items: [...prev.items, ...r.data.items] } : r.data));
    } catch { toast.error('تعذّر التحميل'); } finally { setLoading(false); }
  }, [debounced, visibility]);
  useEffect(() => { load(0); }, [load]);

  const toggle = async (item) => {
    setBusy(item.id);
    try {
      const r = await api.post(`/admin/summaries/${item.id}/visibility`, { hidden: !item.hidden });
      toast.success(r.data.message);
      setData((prev) => {
        const matches = (h) => visibility === 'all' || (visibility === 'hidden') === h;
        const items = prev.items.map((i) => (i.id === item.id ? { ...i, hidden: r.data.hidden } : i)).filter((i) => matches(i.hidden));
        return { ...prev, items, total: prev.total - (prev.items.length - items.length) };
      });
    } catch (e) { toast.error(e.response?.data?.detail || 'فشلت العملية'); } finally { setBusy(''); }
  };

  return (
    <div className="space-y-6" data-testid="admin-summaries-page">
      <h1 className="text-3xl font-bold text-accent flex items-center gap-2"><Quote className="w-7 h-7 text-primary" /> الملخصات والاقتباسات</h1>
      <QueueSettings />
      <Card className="overflow-hidden">
        <div className="p-4 border-b flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالعنوان، المؤلف، القارئ، الرقم، النص..." className="pr-9" data-testid="admin-summaries-search" />
          </div>
          <div className="flex gap-1">
            {[['all', 'الكل'], ['visible', 'الظاهرة'], ['hidden', 'المحجوبة']].map(([id, label]) => (
              <Button key={id} size="sm" variant={visibility === id ? 'default' : 'outline'} onClick={() => setVisibility(id)} data-testid={`admin-summaries-filter-${id}`}>{label}</Button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{data.total} ملخص</span>
        </div>
        {data.items.length === 0 && !loading ? <p className="p-10 text-center text-muted-foreground" data-testid="admin-summaries-empty">لا توجد ملخصات</p>
          : data.items.map((i) => <Row key={i.id} item={i} onToggle={toggle} busy={busy === i.id} />)}
        {loading && <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
        {data.has_more && !loading && (
          <div className="p-4 text-center"><Button variant="outline" onClick={() => load(data.items.length)} data-testid="admin-summaries-more">عرض المزيد</Button></div>
        )}
      </Card>
    </div>
  );
}
